import { context, propagation, SpanStatusCode, trace } from '@opentelemetry/api';
import { store } from '../../store/store';
import { getSystemCode } from '../../utils/getSystemCode';
import { recordSpanError } from '../../utils/otel/enhanceTrace';
import { getRecaptchaToken } from '../recaptcha';

export const apiClient = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const { VITE_BASE_API_URL, VITE_RECAPTCHA_SITE_KEY } = import.meta.env;
  const storeSnapshot = store.getSnapshot();
  const session = storeSnapshot.context.app.session;
  const systemCode = getSystemCode(storeSnapshot.context.urlParams?.rs);
  const activeSpan = trace.getActiveSpan();
  const recaptchaToken = VITE_RECAPTCHA_SITE_KEY
    ? await getRecaptchaToken(VITE_RECAPTCHA_SITE_KEY)
    : '';

  const traceCarrier: Record<string, string> = {};
  propagation.inject(context.active(), traceCarrier);

  const defaultHeaders: Record<string, string> = {
    'content-type': 'application/json',
    'x-date-override': new Date().toISOString(),
    'x-recaptcha-token': recaptchaToken ?? '',
  };

  if (traceCarrier.traceparent) {
    defaultHeaders.traceparent = traceCarrier.traceparent;
  }

  if (traceCarrier.baggage) {
    defaultHeaders.baggage = traceCarrier.baggage;
  }

  if (session?.id) {
    defaultHeaders['x-session-id'] = session.id;
    defaultHeaders['x-interaction-id'] = `${systemCode}:${session.id}`; //interaction ID prefixed with systemCode e.g. DandG_IPR for WIB tracking
  }

  const mergedHeaders = new Headers(defaultHeaders);
  new Headers(options.headers).forEach((value, key) => {
    mergedHeaders.set(key, value);
  });

  let response: Response;

  try {
    response = await fetch(`${VITE_BASE_API_URL}${endpoint}`, {
      ...options,
      headers: mergedHeaders,
    });
  } catch (error) {
    recordSpanError(activeSpan, error);
    throw error;
  }

  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`);
    recordSpanError(activeSpan, error);
    activeSpan?.setStatus({ code: SpanStatusCode.ERROR });
    activeSpan?.setAttribute('http.status_code', response.status);
    throw error;
  }

  return response;
};

export const get = async <T>(endpoint: string, options?: RequestInit): Promise<T> => {
  const response = await apiClient(endpoint, { method: 'GET', ...options });
  return response.json() as Promise<T>;
};

export const post = async <T>(
  endpoint: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<T> => {
  const response = await apiClient(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
    headers,
  });

  const data = await response.json();
  return { ...data, statusCode: response.status } as T;
};
